import React, { useCallback, useEffect, useRef, useState } from 'react';
import './styles.css';
import RoomForm from './components/RoomForm';
import HistoryList from './components/HistoryList';
import CopyToast from './components/CopyToast';
import { CONFIG_OPTIONS, COPY_TOAST_MS, JITSI_BASE_URL } from './config';
import { buildInviteLink } from './lib/link';
import { copyToClipboard } from './lib/clipboard';
import { loadHistory, saveHistory, HISTORY_MAX } from './lib/storage';

function initialParams() {
  const params = {};
  for (const { key, value, defaultOn } of CONFIG_OPTIONS) {
    if (defaultOn) params[key] = value || 'true';
  }
  return params;
}

function App() {
  const [roomName, setRoomName] = useState('');
  const [history, setHistory] = useState(() => loadHistory());
  const [params, setParams] = useState(initialParams);
  const [toast, setToast] = useState({ visible: false, message: '' });

  const toastTimerRef = useRef(null);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const showToast = useCallback((message) => {
    setToast({ visible: true, message });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(
      () => setToast((t) => ({ ...t, visible: false })),
      COPY_TOAST_MS,
    );
  }, []);

  const toggleParam = useCallback((key) => {
    setParams((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        const opt = CONFIG_OPTIONS.find((o) => o.key === key);
        next[key] = opt?.isValueFixed ? opt.value : 'true';
      }
      return next;
    });
  }, []);

  const handleCopy = useCallback(async (link, message) => {
    const ok = await copyToClipboard(link);
    if (ok) {
      showToast(message);
    } else {
      window.prompt('Скопируйте ссылку вручную:', link);
    }
  }, [showToast]);

  const handleCreate = useCallback(async () => {
    const link = buildInviteLink(roomName, params);
    if (!link) return;

    const entry = { room: roomName.trim(), link };
    const deduped = history.filter((h) => h.link !== link);
    const nextHistory = [entry, ...deduped].slice(0, HISTORY_MAX);
    setHistory(nextHistory);
    saveHistory(nextHistory);

    await handleCopy(link, 'Ссылка создана и скопирована в буфер обмена');
  }, [roomName, params, history, handleCopy]);

  const copyExisting = useCallback(
    (link) => handleCopy(link, 'Ссылка скопирована в буфер обмена'),
    [handleCopy],
  );

  const hostname = JITSI_BASE_URL.replace(/^https?:\/\//, '');

  return (
    <>
      <header className="hero">
        <img
          className="hero__logo"
          src="/logo-logistix.png"
          alt="LogistiX"
        />
        <div className="hero__pill">Видеоконференции</div>
        <h1 className="hero__title">Видеоконференции LogistiX</h1>
        <p className="hero__sub">создать приглашение на конференцию</p>
      </header>

      <main className="app">
        <div className="app__card">
          <RoomForm
            roomName={roomName}
            onRoomNameChange={setRoomName}
            params={params}
            onToggleParam={toggleParam}
            onSubmit={handleCreate}
          />
          <CopyToast visible={toast.visible} message={toast.message} />
        </div>

        <HistoryList history={history} onCopy={copyExisting} />
      </main>

      <footer className="footer">© LogistiX · {hostname}</footer>
    </>
  );
}

export default App;
